import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { safeLocal } from '@/utils/safeStorage';
import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  loadOpenAIAdsPixel,
  unloadOpenAIAdsPixel,
} from '@/utils/openaiAdsAttribution';
import './AnalyticsConsent.css';

type ConsentChoice = 'granted' | 'denied';

const STORAGE_KEY = ANALYTICS_CONSENT_STORAGE_KEY;
const PRODUCTION_GA4_ID = 'G-CJRQ8MJVQ4';
let ga4Configured = false;

function privacyIsLimited() {
  const privacyNavigator = navigator as Navigator & { globalPrivacyControl?: boolean };
  return privacyNavigator.globalPrivacyControl === true || navigator.doNotTrack === '1';
}

function readChoice(): ConsentChoice | null {
  const value = safeLocal.get(STORAGE_KEY);
  return value === 'granted' || value === 'denied' ? value : null;
}

function saveChoice(choice: ConsentChoice) {
  safeLocal.set(STORAGE_KEY, choice);
}

function applyConsent(choice: ConsentChoice) {
  window.gtag?.('consent', 'update', {
    analytics_storage: choice,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.dataLayer?.push({ event: 'tsl_consent_update', analytics_consent: choice });
}

function configureAnalytics() {
  if (ga4Configured || typeof window.gtag !== 'function') return;
  window.gtag('config', PRODUCTION_GA4_ID, { send_page_view: false });
  ga4Configured = true;
}

export function AnalyticsConsent() {
  const { pathname } = useLocation();
  const limited = privacyIsLimited();
  const [initialChoice] = useState<ConsentChoice | null>(() => limited ? 'denied' : readChoice());
  const [open, setOpen] = useState(() => !limited && readChoice() === null);

  useEffect(() => {
    if (initialChoice) {
      applyConsent(initialChoice);
      if (initialChoice === 'granted') {
        configureAnalytics();
        loadOpenAIAdsPixel();
      } else {
        unloadOpenAIAdsPixel();
      }
    }
  }, [initialChoice]);

  const update = (next: ConsentChoice) => {
    const effective = limited ? 'denied' : next;
    if (!limited) saveChoice(effective);
    applyConsent(effective);
    setOpen(false);
    if (effective === 'granted') {
      configureAnalytics();
      loadOpenAIAdsPixel();
      window.gtag?.('event', 'page_view', {
        event_source: 'consent_replay',
        page_path: window.location.pathname,
      });
    } else {
      unloadOpenAIAdsPixel();
    }
  };

  // Keep renter overlays off operational controls. Stored consent still applies,
  // and controls return when the operator navigates back to a renter page.
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return null;
  if (pathname === '/intake/ok-to-text' || pathname.endsWith('/intake')) return null;

  return <>
    {open && <section className="tsl-analytics-consent" role="dialog" aria-modal="false" aria-labelledby="tsl-consent-title">
      <strong id="tsl-consent-title">Help us improve Taco Street</strong>
      <p>Allow usage analytics so we can understand which guides and tools help renters. Google Analytics may use an identifier or cookie. We do not send form answers or contact details.</p>
      <div>
        <button className="tsl-consent-accept" type="button" onClick={() => update('granted')}>Accept analytics</button>
        <button type="button" onClick={() => update('denied')}>Decline</button>
      </div>
    </section>}
    {!open && !limited && <button className="tsl-consent-settings" type="button" onClick={() => setOpen(true)}>Analytics settings</button>}
  </>;
}
